import { FEATURE_FLAGS } from "@solomon-apps/config";
import type { OnboardingPurpose } from "@solomon-apps/shared/src/types/clientOnboardingPolicy";
import { MotionConfig } from "motion/react";
import type { ReactElement } from "react";

import { useClientOnboardingApplications } from "@/hooks/useClientOnboardingApplications";
import { OnboardingLite } from "@/pages/OnboardingLite/OnboardingLite";

import { OnboardingHub } from "./OnboardingHub";

/**
 * `/onboarding` entry. Two flags decide what renders, local one first:
 *
 * `FEATURE_FLAGS.clientFullOnboarding` enables the multi-step onboarding hub.
 * When it is off, the route uses Minimum Onboarding (`OnboardingLite`).
 *
 * With the flag on, the original server-owned choice takes over: yaas-api
 * exposes its `onboarding.liteEnabled` config as `liteOnboardingEnabled` on
 * the client application bundle, so the lite form only ever renders where the
 * submit-lite endpoint is actually enabled — the UI cannot drift from the API.
 * An absent flag (older API) falls back to the full flow, the safe direction
 * for that branch.
 *
 * The server flag rides the same applications query `OnboardingGate` blocks on
 * (shared queryKey), so by the time this renders the answer is already in
 * the React Query cache — no extra request, no flash of the wrong flow.
 *
 * `MotionConfig reducedMotion="user"` wraps both branches: this is the sole
 * route component for `/onboarding`, so one wrapper covers every panel
 * entrance, row cross-fade, and celebration stagger beneath it. Transform and
 * layout animation is suppressed for users who ask the OS for reduced motion;
 * opacity cross-fades still run, so nothing appears without warning.
 */
export function OnboardingEntry(): ReactElement {
    const { data, isLoading } = useClientOnboardingApplications();

    // Normally unreachable behind OnboardingGate (it shows its own loading
    // state until this exact query resolves).
    if (isLoading) {
        return <main className="min-h-dvh bg-surface-ground" />;
    }

    const liteEnabled = resolveLiteOnboardingEntry({
        clientFullOnboardingEnabled: FEATURE_FLAGS.clientFullOnboarding,
        liteOnboardingEnabled: data?.data[0]?.liteOnboardingEnabled,
        purpose: data?.data[0]?.application.purpose
    });
    return (
        <MotionConfig reducedMotion="user">
            {liteEnabled ? <OnboardingLite /> : <OnboardingHub />}
        </MotionConfig>
    );
}

/** A server-declared Minting profile has no compatible full-flow surface.
 * Its Lite endpoint remains subject to the server's own submit checks. */
export function resolveLiteOnboardingEntry({
    clientFullOnboardingEnabled,
    liteOnboardingEnabled,
    purpose
}: {
    clientFullOnboardingEnabled: boolean;
    liteOnboardingEnabled?: boolean;
    purpose?: OnboardingPurpose;
}): boolean {
    if (purpose === "minting_only") return true;
    return clientFullOnboardingEnabled
        ? (liteOnboardingEnabled ?? false)
        : true;
}
